import { isNegativeRow } from './matrixRowLookup'

/**
 * Mean of the numeric Input values in a set of response records.
 * Pure function — no Firebase imports.
 *
 * For matrix indicators pass a `mirror` config so negatively-worded rows are
 * flipped within the field scale (higher = better everywhere) — or better,
 * use records that already carry `_mirroredScore` (fetchHierarchyData) or go
 * through useMatrixData. Averaging raw matrix Input without a mirror config
 * dampens/distorts the mean.
 * AI-CONTEXT: See docs/pitfalls.md#database-raw-input-skips-negative-mirroring
 *
 * @typedef {object} MirrorConfig
 * @property {import('./matrixRowLookup').RowLookup} rowLookup - This field's rows, addressable by RowID and RowTitle
 * @property {number} lowerScale - Field scale lower bound (ReachStart)
 * @property {number} upperScale - Field scale upper bound (ReachEnd)
 *
 * @param {Array<{Input?: string|number, RowID?: string, RowTitle?: string}>} records - Questionnaire response records
 * @param {MirrorConfig|null} [mirror] - Mirror negative rows within the field scale
 * @returns {{mean: number|null, n: number}} Mean of numeric inputs and how many counted
 */
const averageNumericInputs = (records, mirror = null) => {
    const scores = (Array.isArray(records) ? records : [])
        .map((r) => {
            if (r?.Input === '' || r?.Input == null) return null
            const num = Number(r.Input)
            if (!Number.isFinite(num)) return null
            const isNegative = !!mirror && isNegativeRow(mirror.rowLookup, r)
            return isNegative ? mirror.lowerScale + mirror.upperScale - num : num
        })
        .filter((v) => v !== null)

    if (scores.length === 0) return { mean: null, n: 0 }
    return { mean: scores.reduce((sum, v) => sum + v, 0) / scores.length, n: scores.length }
}

export default averageNumericInputs
